import { useState, useEffect, useMemo, useRef } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/contexts/AuthContext";
import { trpc } from "@/lib/trpc";

// ─── Command palette (Cmd/Ctrl+K) ──────────────────────────────────────────────
// Navega pra qualquer página, troca de cliente (Raz) e busca pautas, tudo pelo
// teclado. Pedido João set/26 (o estudo apontou que faltava). O primitivo cmdk
// existe no repo, mas aqui é controlado à mão pra ter navegação por teclado
// previsível (setas + Enter + Esc) sem depender da API do cmdk.

type Page = { label: string; href: string; kw?: string; raz?: boolean };
const PAGES: Page[] = [
  { label: "Início", href: "/", kw: "home dashboard" },
  { label: "Pautas", href: "/pautas", kw: "kanban board posts cards" },
  { label: "Linha Editorial", href: "/linha-editorial", kw: "pilares" },
  { label: "Calendário", href: "/calendario", kw: "calendar agenda datas" },
  { label: "Preview Feed", href: "/preview-feed", kw: "feed instagram" },
  { label: "Metas & KPIs", href: "/metas", kw: "kpi objetivos" },
  { label: "Relatórios", href: "/relatorios", kw: "reports numeros" },
  { label: "Direcionador", href: "/direcionador", kw: "regras diretrizes", raz: true },
  { label: "Radar", href: "/radar", kw: "sla alertas parados", raz: true },
  { label: "Recusas", href: "/recusas", kw: "reprovado motivo", raz: true },
  { label: "Usuários", href: "/usuarios", kw: "users equipe" },
  { label: "Convites", href: "/convites", kw: "invite" },
];

type Item =
  | { kind: "page"; id: string; label: string; hint: string; run: () => void }
  | { kind: "client"; id: string; label: string; hint: string; color?: string; run: () => void }
  | { kind: "pauta"; id: string; label: string; hint: string; run: () => void };

export default function CommandPalette({ isRaz, onClose }: { isRaz: boolean; onClose: () => void }) {
  const [, navigate] = useLocation();
  const { setActiveClient } = useAuth();
  const [q, setQ] = useState("");
  const [deb, setDeb] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => { const t = setTimeout(() => setDeb(q.trim()), 200); return () => clearTimeout(t); }, [q]);

  const { data: clients = [] } = trpc.clients.list.useQuery(undefined, { enabled: isRaz, staleTime: 5 * 60_000 });
  const { data: pautas = [], isFetching } = trpc.pautas.search.useQuery(
    { q: deb }, { enabled: deb.length >= 2, staleTime: 30_000 }
  );

  const ql = q.trim().toLowerCase();
  const items = useMemo<Item[]>(() => {
    const out: Item[] = [];
    const pages = PAGES.filter(p => (!p.raz || isRaz) && (!ql || (p.label + " " + (p.kw ?? "")).toLowerCase().includes(ql)));
    for (const p of pages) out.push({ kind: "page", id: `pg:${p.href}`, label: p.label, hint: "Página", run: () => { navigate(p.href); onClose(); } });
    if (isRaz) {
      const cl = (clients as any[])
        .filter(c => c.ativo !== false && (!ql || String(c.nome).toLowerCase().includes(ql)))
        .slice(0, 6);
      for (const c of cl) out.push({
        kind: "client", id: `cl:${c.id}`, label: c.nome, hint: "Trocar cliente", color: c.primaryColor,
        run: () => { setActiveClient(c.id); navigate("/pautas"); onClose(); },
      });
    }
    for (const r of (pautas as any[])) out.push({
      kind: "pauta", id: `pt:${r.id}`, label: r.titulo,
      hint: [isRaz && r.clientNome ? r.clientNome : null, ((r as any).colunaLabel ?? r.coluna) + ((r as any).foraDoQuadro ? " (fora do quadro)" : "")].filter(Boolean).join(" · "),
      run: () => { navigate(`/c/${r.contentId || r.id}`); onClose(); },
    });
    return out;
  }, [ql, isRaz, clients, pautas]);

  useEffect(() => { setActive(0); }, [ql, items.length]);

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive(a => Math.min(a + 1, items.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); items[active]?.run(); }
    else if (e.key === "Escape") { e.preventDefault(); onClose(); }
  };

  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${active}"]`) as HTMLElement | null;
    el?.scrollIntoView({ block: "nearest" });
  }, [active]);

  const ICON: Record<string, string> = { page: "M4 6h16M4 12h16M4 18h16", client: "", pauta: "" };

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center pt-[12vh] px-4" onMouseDown={onClose}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-xl rounded-2xl overflow-hidden bg-[var(--solar-card)] border border-[var(--solar-line)] shadow-[var(--elev-3)]"
        onMouseDown={e => e.stopPropagation()}
      >
        <div className="flex items-center gap-2.5 px-4 border-b border-[var(--solar-line)]">
          <svg className="w-4 h-4 text-[var(--solar-mute)] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
          </svg>
          <input
            ref={inputRef}
            value={q}
            onChange={e => setQ(e.target.value)}
            onKeyDown={onKey}
            placeholder="Buscar página, cliente ou pauta…"
            className="flex-1 py-3.5 bg-transparent text-[15px] text-[var(--solar-ink)] placeholder:text-[var(--solar-mute)] focus:outline-none"
            style={{ fontFamily: "Poppins, sans-serif" }}
          />
          <kbd className="text-[10px] text-[var(--solar-mute)] border border-[var(--solar-line)] rounded px-1.5 py-0.5 shrink-0">Esc</kbd>
        </div>

        <div ref={listRef} className="max-h-[52vh] overflow-y-auto py-1.5">
          {items.length === 0 && (
            <div className="px-4 py-6 text-center text-[13px] text-[var(--solar-mute)]">
              {isFetching ? "Buscando…" : "Nada encontrado."}
            </div>
          )}
          {items.map((it, i) => (
            <button
              key={it.id}
              data-idx={i}
              onMouseEnter={() => setActive(i)}
              onClick={() => it.run()}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors"
              style={{ background: i === active ? "var(--brand-soft)" : "transparent" }}
            >
              <span className="w-6 h-6 rounded-md flex items-center justify-center shrink-0"
                style={{ background: it.kind === "client" ? "transparent" : "var(--solar-bg)", border: it.kind === "client" ? "none" : "1px solid var(--solar-line)" }}>
                {it.kind === "client"
                  ? <span className="w-2.5 h-2.5 rounded-full" style={{ background: (it as any).color || "var(--solar-mute)" }} />
                  : it.kind === "pauta"
                  ? <svg className="w-3.5 h-3.5 text-[var(--solar-mute)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                  : <svg className="w-3.5 h-3.5 text-[var(--solar-mute)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" /></svg>}
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-[13.5px] text-[var(--solar-ink)] font-medium truncate">{it.label}</span>
                {it.hint && <span className="block text-[11px] text-[var(--solar-mute)] truncate">{it.hint}</span>}
              </span>
              {i === active && (
                <kbd className="text-[10px] text-[var(--brand-ink)] shrink-0" style={{ fontFamily: "Poppins" }}>↵</kbd>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
